import { useEffect, useState } from 'react';
import { createApi } from '../services/api.js';

interface Sender {
  id: string;
  sender_hash: string;
  sender_display: string;
  total_emails_analyzed: number;
  total_flagged: number;
  avg_toxicity_score: number;
  relationship_tag: string | null;
}

interface SenderAnalysis {
  id: string;
  toxicity_score: number;
  primary_category: string;
  primary_severity: string;
  analyzed_at: string;
}

function scoreStyle(score: number) {
  if (score <= 20) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500', label: 'Clean' };
  if (score <= 40) return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', bar: 'bg-yellow-500', label: 'Mild' };
  if (score <= 60) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', bar: 'bg-orange-500', label: 'Warning' };
  if (score <= 75) return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', bar: 'bg-red-500', label: 'Toxic' };
  return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', bar: 'bg-red-600', label: 'Critical' };
}

function formatCategory(cat: string) {
  if (!cat || cat === 'none') return 'Clean';
  return cat.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getTrend(analyses: SenderAnalysis[]): { direction: 'up' | 'down' | 'stable'; label: string } {
  if (analyses.length < 3) return { direction: 'stable', label: 'Not enough data' };
  const half = Math.floor(analyses.length / 2);
  const recent = analyses.slice(0, half);
  const earlier = analyses.slice(half);
  const recentAvg = recent.reduce((s, a) => s + a.toxicity_score, 0) / recent.length;
  const earlierAvg = earlier.reduce((s, a) => s + a.toxicity_score, 0) / earlier.length;
  const diff = recentAvg - earlierAvg;
  if (diff > 5) return { direction: 'up', label: `Escalating (${Math.round(earlierAvg)} → ${Math.round(recentAvg)})` };
  if (diff < -5) return { direction: 'down', label: `Improving (${Math.round(earlierAvg)} → ${Math.round(recentAvg)})` };
  return { direction: 'stable', label: `Stable (~${Math.round(recentAvg)})` };
}

export function SendersPage({ token }: { token: string }) {
  const [senders, setSenders] = useState<Sender[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [senderAnalyses, setSenderAnalyses] = useState<Record<string, SenderAnalysis[]>>({});

  useEffect(() => {
    const api = createApi(token);
    api.getSenders().then((res) => {
      if (res.ok) setSenders(res.data);
      setLoading(false);
    });
  }, [token]);

  async function toggleSender(senderId: string) {
    if (expanded === senderId) {
      setExpanded(null);
      return;
    }
    setExpanded(senderId);
    if (!senderAnalyses[senderId]) {
      const api = createApi(token);
      const res = await api.getSenderAnalyses(senderId, 30);
      if (res.ok) {
        setSenderAnalyses(prev => ({ ...prev, [senderId]: res.data }));
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-300 border-t-brand" />
      </div>
    );
  }

  const flaggedSenders = senders.filter(s => s.avg_toxicity_score > 40);
  const cleanSenders = senders.filter(s => s.avg_toxicity_score <= 40);

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-xl font-bold text-ink">Sender Profiles</h1>
        <p className="text-sm text-ink-muted mt-1">
          Track communication patterns from people who email you
        </p>
      </div>

      {senders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-surface-200 bg-white p-12 text-center">
          <p className="text-sm font-medium text-ink">No sender data yet</p>
          <p className="text-xs text-ink-light mt-1">Analyze some emails to start building sender profiles.</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-surface-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Total Senders</p>
              <p className="mt-2 text-3xl font-black text-ink tabular-nums">{senders.length}</p>
            </div>
            <div className="rounded-xl border border-red-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Flagged</p>
              <p className={`mt-2 text-3xl font-black tabular-nums ${flaggedSenders.length > 0 ? 'text-red-600' : 'text-ink'}`}>
                {flaggedSenders.length}
              </p>
              <p className="text-[11px] text-ink-light mt-1">avg score &gt; 40</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Clean</p>
              <p className="mt-2 text-3xl font-black text-emerald-600 tabular-nums">{cleanSenders.length}</p>
              <p className="text-[11px] text-ink-light mt-1">avg score ≤ 40</p>
            </div>
          </div>

          {/* Sender list */}
          <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-surface-100 flex items-center justify-between">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">
                All Senders — sorted by avg toxicity
              </p>
            </div>
            <div className="divide-y divide-surface-100">
              {senders.map((sender) => {
                const style = scoreStyle(sender.avg_toxicity_score);
                const isExpanded = expanded === sender.id;
                const analyses = senderAnalyses[sender.id];
                const trend = analyses ? getTrend(analyses) : null;

                return (
                  <div key={sender.id}>
                    <button
                      onClick={() => toggleSender(sender.id)}
                      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-surface-50 transition-colors text-left"
                    >
                      {/* Avg score */}
                      <div className={`shrink-0 w-12 h-12 rounded-xl ${style.bg} flex flex-col items-center justify-center`}>
                        <span className={`text-lg font-black tabular-nums leading-none ${style.text}`}>
                          {Math.round(sender.avg_toxicity_score)}
                        </span>
                        <span className={`text-[9px] font-medium ${style.text}`}>avg</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-ink truncate">
                            {sender.sender_display || sender.sender_hash.slice(0, 8) + '...'}
                          </span>
                          <span className={`rounded-full ${style.bg} ${style.text} px-2 py-0.5 text-[10px] font-bold`}>
                            {style.label}
                          </span>
                          {sender.relationship_tag && (
                            <span className="rounded-full bg-surface-100 text-ink-muted px-2 py-0.5 text-[10px]">
                              {sender.relationship_tag}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-ink-light mt-0.5">
                          {sender.total_emails_analyzed} analyzed · {sender.total_flagged} flagged
                        </p>
                      </div>

                      {/* Score bar */}
                      <div className="hidden sm:block w-20">
                        <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${style.bar}`}
                            style={{ width: `${Math.min(sender.avg_toxicity_score, 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Expand arrow */}
                      <svg
                        className={`h-4 w-4 text-ink-light transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="px-5 pb-4 bg-surface-50 border-t border-surface-100">
                        {!analyses ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-surface-300 border-t-brand" />
                          </div>
                        ) : analyses.length === 0 ? (
                          <p className="text-xs text-ink-light py-4 text-center">No analyses found</p>
                        ) : (
                          <>
                            {/* Trend indicator */}
                            {trend && (
                              <div className={`mt-3 mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
                                trend.direction === 'up'
                                  ? 'bg-red-50 text-red-700'
                                  : trend.direction === 'down'
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-surface-100 text-ink-muted'
                              }`}>
                                <span className="text-base">
                                  {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
                                </span>
                                {trend.label}
                              </div>
                            )}

                            {/* Mini score timeline */}
                            <div className="flex items-end gap-1 h-12 mt-2 mb-3">
                              {analyses.slice(0, 20).reverse().map((a, i) => {
                                const s = scoreStyle(a.toxicity_score);
                                return (
                                  <div
                                    key={a.id || i}
                                    className={`flex-1 rounded-t ${s.bar} transition-all`}
                                    style={{ height: `${Math.max(a.toxicity_score, 4)}%` }}
                                    title={`Score: ${a.toxicity_score} — ${timeAgo(a.analyzed_at)}`}
                                  />
                                );
                              })}
                            </div>

                            {/* Recent analyses list */}
                            <div className="space-y-1">
                              {analyses.slice(0, 8).map((a) => {
                                const s = scoreStyle(a.toxicity_score);
                                return (
                                  <div key={a.id} className="flex items-center gap-3 rounded-lg bg-white px-3 py-2">
                                    <span className={`text-xs font-bold tabular-nums w-6 text-right ${s.text}`}>
                                      {a.toxicity_score}
                                    </span>
                                    <div className="w-12 h-1 bg-surface-100 rounded-full overflow-hidden">
                                      <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${a.toxicity_score}%` }} />
                                    </div>
                                    <span className="text-xs text-ink-muted flex-1">{formatCategory(a.primary_category)}</span>
                                    <span className="text-[11px] text-ink-light tabular-nums">{timeAgo(a.analyzed_at)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-center text-[11px] text-surface-300 mt-6">
            Sender identities are hashed. AAS never stores real email addresses.
          </p>
        </>
      )}
    </div>
  );
}
